import {
    Avatar,
    Box,
    CircularProgress,
    Container,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Card, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { UrlConstant } from "../../../commom/UrlConstant";
import { Crypto } from '../../../types/crypto';
import Axios from "../../../config/axios";
import RightSideSection from "../SharedComponent/RightSideSection";
import { CommonDataTable } from "../SharedComponent/CommonDataTable";
import ThunderIconCirlcle from "../SvgIcons/ThunderIconCirlcle";
import { useAppSelector } from "../../../Redux/hooks";
import { extractQuoteWithFallback } from "../../../helpers/cryptoQuoteHelper";
import { formatPercentage } from "../../../utils/formatting";
import { usePriceFormatter } from "../../../hooks";

function renderPercentChange(value?: number) {
    if (typeof value !== "number") return <span>N/A</span>;
    let color = "#B0B3B8";
    if (value > 0) {
        color = "#4edf6d";

    } else if (value < 0) {
        color = "red";
    }
    return (
        <span style={{ color }} >
            {formatPercentage(value, { showSign: true })}
        </span>
    );
}

function CryptoMarket() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [cryptos, setCryptos] = useState<Crypto[]>([]);
    const [loading, setLoading] = useState(false);
    const showOnlyLoanEligible = useAppSelector((state) => state.cryptoFilter.showOnlyLoanEligible);
    const currencyInfo = useAppSelector((state) => state.currency);
    const currencyKey = currencyInfo.effectiveCurrency || 'USD';
    const filteredCryptos = React.useMemo(() => {
        if (showOnlyLoanEligible) {
            return cryptos.filter(crypto => crypto.isLoanAvailable === true);
        }
        return cryptos;
    }, [cryptos, showOnlyLoanEligible]);
    const formatter = usePriceFormatter()
    const columns = [
        {
            name: "Name",
            selector: (row: Crypto) => row.symbol,
            cell: (row: Crypto) => (
                <div
                    className="stock-label"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                    }}
                >
                    <Avatar
                        src={row.logo}
                        alt={row.symbol}
                        sx={{
                            width: 30,
                            height: 30,
                            backgroundColor: "white",
                            objectFit: "fill",
                        }}
                    >
                        {row.symbol?.[0] || "₿"}
                    </Avatar>
                    <span>{row.symbol}</span>
                    {row.isLoanAvailable && (
                        <Tooltip title="Booster Loan Eligible" arrow placement="top">
                            <span>
                                <ThunderIconCirlcle
                                    width={18}
                                    height={18}
                                />
                            </span>
                        </Tooltip>

                    )}
                </div>
            ),
            sortable: true,
            center: false,
            width: "200px",
        },
        {
            name: "Sign",
            selector: (row: Crypto) => row.name,
            cell: (row: Crypto) => (
                <span style={{
                    display: 'inline-block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {row.name}
                </span>
            ),
            sortable: true,
            center: false,
            width: "200px",
        },
        {
            name: "Price",
            selector: (row: Crypto) => {
                const quote = extractQuoteWithFallback(row as any, currencyKey);
                return quote?.price ?? 0;
            },
            cell: (row: Crypto) => {
                const quote = extractQuoteWithFallback(row as any, currencyKey);
                return <span>{formatter.price(quote?.price).display ?? 'N/A'}</span>;
            },
            sortable: true,
            center: true,
            width: "150px",
        },
        {
            name: "1h%",
            selector: (row: Crypto) => {
                const quote = extractQuoteWithFallback(row as any, currencyKey);
                return quote?.percent_change_1h ?? 0;
            },
            cell: (row: Crypto) => {
                const quote = extractQuoteWithFallback(row as any, currencyKey);
                return renderPercentChange(quote?.percent_change_1h);
            },
            sortable: true,
            center: true,
            width: "120px",
        },
        {
            name: "24h%",
            selector: (row: Crypto) => {
                const quote = extractQuoteWithFallback(row as any, currencyKey);
                return quote?.percent_change_24h ?? 0;
            },
            cell: (row: Crypto) => {
                const quote = extractQuoteWithFallback(row as any, currencyKey);
                return renderPercentChange(quote?.percent_change_24h);
            },
            sortable: true,
            center: true,
            width: "120px",
        },
        // {
        //     name: "Market Cap",
        //     selector: (row: Crypto) => row.quote?.USD?.market_cap,
        //     cell: (row: Crypto) => row.quote?.USD?.market_cap ? `$${formatPrice(row.quote.USD.market_cap)}` : 'N/A',
        //     sortable: true,
        //     center: true,
        //     width: "250px",
        // },
        // {
        //     name: "Volume (24h)",
        //     selector: (row: Crypto) => row.quote?.USD?.volume_24h,
        //     cell: (row: Crypto) => row.quote?.USD?.volume_24h ? `$${formatPrice(row.quote.USD.volume_24h)}` : 'N/A',
        //     sortable: true,
        //     center: true,
        //     width: "250px",
        // },
        // {
        //     name: "Circulating Supply",
        //     selector: (row: Crypto) => row.circulating_supply,
        //     cell: (row: Crypto) => row.circulating_supply ? `${formatPrice(row.circulating_supply)} ${row.symbol}` : 'N/A',
        //     sortable: true,
        //     center: true,
        //     width: "180px",
        // },
    ];

    const navigateToDetail = (row: Crypto) => {
        navigate("/crypto-detail", {
            state: {
                externalID: row.cmc_id
            },
        });
    };

    const fetchCryptos = async () => {
        setLoading(true);
        try {
            const url = UrlConstant.Crypto.GetCryptoList

            const response = await Axios.get(url);
            const list = (response.data.data || []).slice();
            list.sort((a: any, b: any) => {
                const aQuote = extractQuoteWithFallback(a, currencyKey);
                const bQuote = extractQuoteWithFallback(b, currencyKey);
                const aCap = aQuote?.market_cap ?? 0;
                const bCap = bQuote?.market_cap ?? 0;
                return bCap - aCap; // Descending by market cap
            });
            setCryptos(list);
            setTotalRows(response.data.data.totalCount || 0);
        } catch (error) {
            setCryptos([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCryptos();
        // eslint-disable-next-line
    }, [page, rowsPerPage]);

    return (
        <Box className="bg-color" display="flex">
            <Container
                maxWidth={false}
                className="bg-color p-3 px-5"
                style={{
                    minHeight: "calc(100vh -  71px - 143px)",
                    width: "70%",
                }}
            >
                <Typography
                    className="text-secondary"
                    sx={{ marginBottom: "20px", paddingTop: "30px" }}
                >
                    <span
                        onClick={() => navigate("/dashboard")}
                        style={{ color: "#4A7AF2", cursor: "pointer" }}
                    >
                        Dashboard
                    </span>{" "}
                    / Crypto
                </Typography>
                <Box mb={5}>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Typography
                            className="text-white"
                            mb={0}
                            style={{ fontSize: "1.5rem" }}
                        >
                            Crypto
                        </Typography>
                    </Box>
                </Box>
                <Container style={{ width: "100%", left: "0", margin: "0" }} className="p-0">
                    <Row className="dark-tbl no-border p-0">
                        <Card className="custom-card overflow-hidden bg-color w-100">
                            <Card.Body className="ps-12 p-2">
                                <div>
                                    {loading ? (
                                        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                                            <CircularProgress color="primary" />
                                        </Box>
                                    ) : (
                                        <CommonDataTable
                                            setPage={setPage}
                                            setRowsPerPage={setRowsPerPage}
                                            data={filteredCryptos}
                                            totalRows={totalRows}
                                            columns={columns}
                                            title={"Crypto"}
                                            rowNavigateTo={(row) => ({
                                                url: "/crypto-detail",
                                                state: {
                                                    externalID: row.cmc_id
                                                },
                                            })}
                                        />
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Row>
                </Container>
            </Container>
            <Container style={{ width: "40%", paddingTop: "20px" }}>
                <RightSideSection />
            </Container>
        </Box>
    );
}

export default CryptoMarket; 